import React from "react";
import { Observer, observer } from "mobx-react";
import { observable } from "mobx";
import ReactResizeDetector from "react-resize-detector";

import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";

import { Badge, Button, Collapse, Paper } from "@material-ui/core";
import Grow from "@material-ui/core/Grow";

import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";

import SvgI24Check from "assets/icons/I24Check";
import SvgI24Filter from "assets/icons/I24Filter";

import OutsideClickHandler from "components/OutsideClickHandler";

import { getActiveProgramTypeText, getPublishStatusText } from "utils/publish";

import { ACTIVE_PROGRAM_TYPE, PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS, FILTER_COLUMNS_TEXT } from "constants/filterConstants";
import { TableFiltersType } from "stores/templates/TableStore";

import {
  Popper,
  Container,
  MenuList,
  CheckedIcon,
  IconContainer,
  IconGap,
  MenuItemWrapper,
  MenuItem,
  Buttons,
  ButtonContainer,
} from "./styles";

type Props = {
  initValue?: TableFiltersType;
  accessProgramsFiltering?: boolean;
  statusFiltering?: boolean;
  activeProgramTypeFiltering?: boolean;
  onApply: (values: any[]) => void;
  onClear: () => void;
};

type FilterItem = { value: string; label?: string; selected: boolean };

type FilterGroup = {
  column: string;
  text: string;
  isNestedMenuOpened: boolean;
  filterItems?: FilterItem[];
};

@observer
class FilteringControl extends React.Component<Props> {
  @observable isMenuOpened: boolean = false;
  @observable filtersState: FilterGroup[] = [];

  ref = React.createRef<HTMLButtonElement>();
  popperRef = React.createRef<any>();

  get appliedFiltersCount() {
    const { initValue } = this.props;

    return initValue
      ? (initValue?.[FILTER_COLUMNS.PUBLISH_STATUS]?.length ?? 0) +
          (initValue?.[FILTER_COLUMNS.ACTIVE_PROGRAMS]?.length ?? 0) +
          (initValue?.[FILTER_COLUMNS.ACTIVE_PROGRAM_TYPE]?.length ?? 0)
      : undefined;
  }

  get isOnlyOneFilterGroup() {
    const {
      accessProgramsFiltering,
      statusFiltering,
      activeProgramTypeFiltering,
    } = this.props;

    return (
      [
        accessProgramsFiltering,
        statusFiltering,
        activeProgramTypeFiltering,
      ].filter(Boolean).length === 1
    );
  }

  async componentDidMount() {
    if (ActiveProgramDictionary.items.length === 0) {
      await ActiveProgramDictionary.fetch();
    }

    this.initFiltersState();
  }

  initFiltersState = () => {
    const {
      accessProgramsFiltering,
      statusFiltering,
      activeProgramTypeFiltering,
      initValue,
    } = this.props;

    const publishStatusFilters = statusFiltering
      ? {
          column: FILTER_COLUMNS.PUBLISH_STATUS,
          text: FILTER_COLUMNS_TEXT.PUBLISH_STATUS,
          isNestedMenuOpened:
            this.isOnlyOneFilterGroup ||
            !!initValue?.[FILTER_COLUMNS.PUBLISH_STATUS]?.length,
          filterItems: Object.values(PUBLISH_STATUSES).map(
            (publishStatus: string) => ({
              value: publishStatus,
              label: getPublishStatusText(publishStatus),
              selected: initValue?.[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
                publishStatus,
              ),
            }),
          ),
        }
      : undefined;
    const accessProgramsFilters = accessProgramsFiltering
      ? {
          column: FILTER_COLUMNS.ACTIVE_PROGRAMS,
          text: FILTER_COLUMNS_TEXT.ACTIVE_PROGRAMS,
          isNestedMenuOpened:
            this.isOnlyOneFilterGroup ||
            !!initValue?.[FILTER_COLUMNS.ACTIVE_PROGRAMS]?.length,
          filterItems: ActiveProgramDictionary.options.map(option => ({
            ...option,
            selected: initValue?.[FILTER_COLUMNS.ACTIVE_PROGRAMS]?.includes(
              option.value,
            ),
          })),
        }
      : undefined;
    const activeProgramTypesFilters = activeProgramTypeFiltering
      ? {
          column: FILTER_COLUMNS.ACTIVE_PROGRAM_TYPE,
          text: FILTER_COLUMNS_TEXT.ACTIVE_PROGRAM_TYPE,
          isNestedMenuOpened:
            this.isOnlyOneFilterGroup ||
            !!initValue?.[FILTER_COLUMNS.ACTIVE_PROGRAM_TYPE]?.length,
          filterItems: Object.values(ACTIVE_PROGRAM_TYPE).map(
            (activeProgramType: string) => ({
              value: activeProgramType,
              label: getActiveProgramTypeText(activeProgramType),
              selected: initValue?.[
                FILTER_COLUMNS.ACTIVE_PROGRAM_TYPE
              ]?.includes(activeProgramType),
            }),
          ),
        }
      : undefined;

    this.filtersState = [
      publishStatusFilters,
      accessProgramsFilters,
      activeProgramTypesFilters,
    ].filter(Boolean);
  };

  getIsChecked = (filterItem: FilterItem) => {
    return filterItem.selected;
  };

  getIsNestedMenuOpened = (filterGroup: FilterGroup) => {
    return this.filtersState.find(
      filterStateGroup => filterStateGroup.column === filterGroup.column,
    )?.isNestedMenuOpened;
  };

  toggleMenu = () => {
    this.isMenuOpened = !this.isMenuOpened;
    if (this.isMenuOpened) {
      this.initFiltersState();
    }
  };

  openNestedMenu = (filterGroup: FilterGroup) => () => {
    if (this.isOnlyOneFilterGroup) {
      return;
    }

    const index = this.filtersState.findIndex(
      filterStateGroup => filterStateGroup.column === filterGroup.column,
    );

    if (index !== -1) {
      this.filtersState[index].isNestedMenuOpened = !this.filtersState[index]
        .isNestedMenuOpened;
    }
  };

  onMenuClick = (filterGroup: FilterGroup, filterItem: FilterItem) => () => {
    const index = this.filtersState.findIndex(
      filterState => filterState.column === filterGroup.column,
    );

    if (index === -1) {
      return;
    }

    const items = this.filtersState[index].filterItems ?? [];
    const itemIndex = items.findIndex(item => item.value === filterItem.value);

    if (itemIndex === -1) {
      return;
    }

    items[itemIndex].selected = !items[itemIndex].selected;
    this.filtersState[index].filterItems = items;
  };

  closeMenu = () => {
    this.isMenuOpened = false;
  };

  onApplyFilters = () => {
    const { onApply } = this.props;

    const values = this.filtersState.map(filterGroup => ({
      column: filterGroup.column,
      value: filterGroup.filterItems
        ?.filter(({ selected }: { selected: boolean }) => selected)
        .map(({ value }) => value),
    }));

    onApply(values);
    this.closeMenu();
  };

  onClearFilters = () => {
    const { onClear } = this.props;

    onClear();
    this.closeMenu();
  };

  updatePopperPosition = () => {
    this.popperRef?.current?.update();
  };

  render() {
    return (
      <>
        <Badge
          overlap="rectangular"
          badgeContent={this.appliedFiltersCount}
          color="primary"
        >
          <Button
            startIcon={<SvgI24Filter />}
            variant="text"
            color="secondary"
            onClick={this.toggleMenu}
            ref={this.ref}
          >
            Filter
          </Button>
        </Badge>
        <Popper
          open={this.isMenuOpened}
          anchorEl={this.ref.current}
          popperRef={this.popperRef}
          placement={"left-start"}
          modifiers={{
            preventOverflow: {
              enabled: true,
              boundariesElement: "viewport",
            },
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Observer>
              {() => (
                <Grow {...TransitionProps}>
                  <Paper>
                    <OutsideClickHandler onClick={this.closeMenu}>
                      <ReactResizeDetector
                        onResize={this.updatePopperPosition}
                        refreshMode={"debounce"}
                        refreshRate={500}
                        skipOnMount
                      >
                        <Container>
                          <MenuList>
                            {this.filtersState.map(filterGroup => (
                              <MenuItemWrapper
                                key={`${filterGroup.column}_${filterGroup.text}`}
                              >
                                <MenuItem
                                  onClick={this.openNestedMenu(filterGroup)}
                                >
                                  {filterGroup.text}
                                  {!this.isOnlyOneFilterGroup && (
                                    <IconContainer>
                                      {this.getIsNestedMenuOpened(
                                        filterGroup,
                                      ) ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </IconContainer>
                                  )}
                                </MenuItem>
                                <Collapse
                                  in={this.getIsNestedMenuOpened(filterGroup)}
                                >
                                  {filterGroup.filterItems?.map(filterItem => (
                                    <MenuItem
                                      onClick={this.onMenuClick(
                                        filterGroup,
                                        filterItem,
                                      )}
                                      key={filterItem.value}
                                      isNestedMenu
                                    >
                                      {filterItem.label}
                                      <IconContainer>
                                        {this.getIsChecked(filterItem) ? (
                                          <CheckedIcon>
                                            <SvgI24Check />
                                          </CheckedIcon>
                                        ) : (
                                          <IconGap />
                                        )}
                                      </IconContainer>
                                    </MenuItem>
                                  ))}
                                </Collapse>
                              </MenuItemWrapper>
                            ))}
                          </MenuList>

                          <Buttons>
                            <ButtonContainer>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={this.onClearFilters}
                                fullWidth
                              >
                                Clear
                              </Button>
                            </ButtonContainer>
                            <ButtonContainer>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={this.onApplyFilters}
                                fullWidth
                              >
                                Apply
                              </Button>
                            </ButtonContainer>
                          </Buttons>
                        </Container>
                      </ReactResizeDetector>
                    </OutsideClickHandler>
                  </Paper>
                </Grow>
              )}
            </Observer>
          )}
        </Popper>
      </>
    );
  }
}

export default FilteringControl;
